import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
  Marker,
  Popup,
  useMap,
  WMSTileLayer,
} from "react-leaflet";
import { Box, IconButton } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Information from "./Information";
import * as turf from '@turf/turf';
import { Typography } from '@mui/material';
import CommonTable from "../../../common/CommonTable";
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";

// --- Configuration and Helper Functions ---
const INITIAL_VIEW_STATE = {
  longitude: 125.7275, 
  latitude: -8.8742,
  zoom: 8, 
};

// GeoServer WMS URL for Fire Risk layers
const GEOSERVER_WMS_URL = "https://drr-dss.rimes.int/geoserver/fire-risk/wms";

const getFormattedDateForAPI = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFormattedDateForTIFF = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// ── Dates ───────────────────────────────────────────────────────────────────
// "Today" is the browser's current local date at midnight; the picked date is
// sent to FIRMS as-is (one day). No time-zone conversion.
const getTodayDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

// FIRMS acq_time is HHMM (e.g. 545 = 05:45 UTC). Returns {h, m}.
const splitHHMM = (acqTime) => {
  const n = Math.floor(Number(acqTime));
  if (!Number.isFinite(n) || n < 0) return null;
  return { h: Math.floor(n / 100), m: n % 100 };
};

const DATA_START_DATE_OBJ = new Date("2024-01-01T00:00:00"); 

const pulseIconStyles = `
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
}

.firms-dot-icon {
  background-color: red;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid white;
  animation: pulse 1.5s infinite;
}
`;


const loaderStyles = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.circle-loader-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 999;
}
.circle-loader {
    border: 8px solid #f3f3f3;
    border-top: 8px solid #3498db;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
}
.loading-text {
    color: #333;
    font-weight: bold;
}
`;

const CircleLoader = () => (
  <div className="circle-loader-container">
    <div className="circle-loader"></div>
    <div className="loading-text">Loading Fire Data...</div>
  </div>
);

const parseCSV = (csvString) => {
  const [headerLine, ...dataLines] = csvString.trim().split('\n');
  const headers = headerLine.split(',');

  return dataLines.map(line => {
    const values = line.split(',');
    const row = {};
    headers.forEach((header, i) => {
      const cleanHeader = header.trim();
      const value = (values[i] ?? '').trim(); // short/odd lines (e.g. an API error message) must not throw
      
      if (['latitude', 'longitude', 'bright_ti4', 'bright_ti5', 'scan', 'track', 'frp', 'acq_time'].includes(cleanHeader)) {
          row[cleanHeader] = parseFloat(value);
      } else {
          row[cleanHeader] = value;
      }
    });
    if (typeof row.latitude === 'number' && typeof row.longitude === 'number') {
        return row;
    }
    return null;
  }).filter(row => row !== null);
};

const formatUTCTime = (acqTime) => {
  const hm = splitHHMM(acqTime);
  if (!hm) return 'N/A';
  return `${String(hm.h).padStart(2, '0')}:${String(hm.m).padStart(2, '0')} UTC`;
};

const getConfidenceText = (code) => {
    switch (String(code).toLowerCase()) {
        case 'n':
            return 'Nominal';
        case 'l':
            return 'Low';
        case 'h':
            return 'High';
        default:
            return 'N/A';
    }
};

// Confidence order used for sorting: High, then Nominal, then Low (N/A last).
// Ranks start at 1 because the table treats falsy sort values as empty.
const CONFIDENCE_RANK = { High: 1, Nominal: 2, Low: 3 };
const confidenceRank = (text) => CONFIDENCE_RANK[text] || 4;

const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.flyToBounds(bounds);
    }
  }, [map, bounds]);
  return null;
};

const createPulseIcon = () => {
    const iconHtml = `<div class="firms-dot-icon"></div>`;
    return L.divIcon({
        html: iconHtml,
        className: 'firms-dot-icon-wrapper',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
    });
};

const Firms = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [fireData, setFireData] = useState([]); 
  const [sucoFireReports, setSucoFireReports] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(getTodayDate()); 
  const [mapFooterDescription, setMapFooterDescription] = useState("");
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  const [showForecastLayer, setShowForecastLayer] = useState(true); 
  const [showFirmsLayer, setShowFirmsLayer] = useState(true);

  const [mapBounds, setMapBounds] = useState(null);

  const filterPanelRef = useRef(null);

  const firmsMapKey = "547942f5db74ff44f3bde13d5e6109fe"; 
  const satellite = "VIIRS_SNPP_NRT";

  const fetchFireData = async (date) => {
    if (!date) return;
    const dateString = getFormattedDateForAPI(date);

    const regionBoundingBox = "124.0410,-9.5000,127.5000,-8.0500";
    const apiUrl = `https://firms.modaps.eosdis.nasa.gov/api/area/csv/${firmsMapKey}/${satellite}/${regionBoundingBox}/1/${dateString}`;

    setLoading(true);
    setMapFooterDescription(`Loading data for: ${dateString}...`);
    setFireData([]);
    setSucoFireReports([]);

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
          console.error(`CSV Fetch Error: ${response.status} for ${dateString}. Key might be invalid or data unavailable.`);
          setMapFooterDescription(`Error fetching data for ${dateString}. Try another date.`);
          throw new Error(`Failed to fetch data`);
      }
      const csvText = await response.text();
      setFireData(parseCSV(csvText));
      setMapFooterDescription(`VIIRS Fire Data: ${dateString}`);
    } catch (error) {
      setMapFooterDescription(`Error fetching data for ${dateString}. Try another date.`);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const fetchBoundaryData = async () => {
      try {
        const boundaryResponse = await fetch("/DATA/OSMbased_adm3_boundary.json");
        const boundaryJson = await boundaryResponse.json();
        setBoundaryData(boundaryJson);
        
        if (boundaryJson && boundaryJson.features) {
            const geoJsonLayer = L.geoJSON(boundaryJson);
            const bounds = geoJsonLayer.getBounds();
            if (bounds.isValid()) {
                setMapBounds(bounds);
            }
        }

      } catch (error) {
        console.error("Error loading boundary data:", error);
      }
    };
    fetchBoundaryData();
  }, []); 
  
  useEffect(() => {
    fetchFireData(selectedDate);
  }, [selectedDate]); 

  const fetchSucoFireReports = useCallback(async (fireData, boundaryData) => {
    if (fireData.length === 0 || !boundaryData) {
        setSucoFireReports([]);
        return;
    }

    const reports = [];
    fireData.forEach((firePoint) => {
        const point = turf.point([firePoint.longitude, firePoint.latitude]);
        for (let i = 0; i < boundaryData.features.length; i++) {
            const sucoFeature = boundaryData.features[i];
            if (turf.booleanPointInPolygon(point, sucoFeature)) {
                reports.push({
                    sNo: reports.length + 1,
                    ADM1: sucoFeature.properties.ADM1,
                    ADM2: sucoFeature.properties.ADM2,
                    ADM3: sucoFeature.properties.ADM3,
                    latitude: firePoint.latitude,
                    longitude: firePoint.longitude,
                    acq_time: firePoint.acq_time,
                    confidence: firePoint.confidence,
                    bright_ti4: firePoint.bright_ti4,
                    bright_ti5: firePoint.bright_ti5,
                });
                break;
            }
        }
    });
    setSucoFireReports(reports);
  }, []);

  useEffect(() => {
    fetchSucoFireReports(fireData, boundaryData);
  }, [fireData, boundaryData, fetchSucoFireReports]);

  const handleCalendarDateChange = (date) => {
    date.setHours(0, 0, 0, 0); 
    
    setSelectedDate(date);
    setShowFilterPanel(false);

    // This is no longer based on a hardcoded list, we'll assume a WMS layer is available for any date
    setShowForecastLayer(true);
  };
  
  const isDateAvailable = (date) => {
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = getTodayDate();
    
    return normalizedDate >= DATA_START_DATE_OBJ && normalizedDate <= today;
  };
  
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = pulseIconStyles + loaderStyles;
    document.head.appendChild(styleSheet);

    return () => {
        document.head.removeChild(styleSheet);
    };
  }, []);

  // Same shared table the Landslide forecast page uses (search, sort, paging,
  // column toggle and CSV export are built in). Confidence is carried in the
  // `advisory` field so the table's level filter works on it.
  const tableData = useMemo(
    () => [...sucoFireReports]
      // High, then Nominal, then Low; within each level, earliest detection first.
      .sort((a, b) =>
        confidenceRank(getConfidenceText(a.confidence)) - confidenceRank(getConfidenceText(b.confidence)) ||
        (a.acq_time || 0) - (b.acq_time || 0)
      )
      .map((r) => ({
      Suco: r.ADM3,
      PostAdmin: r.ADM2,
      Municipality: r.ADM1,
      time: formatUTCTime(r.acq_time),
      advisory: getConfidenceText(r.confidence),
      bright4: typeof r.bright_ti4 === 'number' ? r.bright_ti4.toFixed(2) : 'N/A',
      bright5: typeof r.bright_ti5 === 'number' ? r.bright_ti5.toFixed(2) : 'N/A',
      lat: typeof r.latitude === 'number' ? r.latitude.toFixed(4) : 'N/A',
      lng: typeof r.longitude === 'number' ? r.longitude.toFixed(4) : 'N/A',
    })),
    [sucoFireReports],
  );

  const confidenceColors = {
    High: "rgb(230, 45, 58)",
    Nominal: "rgb(243, 155, 58)",
    Low: "rgb(243, 235, 18)",
    "N/A": "rgb(222, 222, 223)",
    All: "#f0f0f0",
  };

  const columns = [
    { key: "SlNo", label: "Sl No" },
    { key: "Suco", label: "Suco", sortable: true },
    { key: "PostAdmin", label: "Post Admin", sortable: true },
    { key: "Municipality", label: "Municipality", sortable: true },
    { key: "time", label: "Time (UTC)", sortable: true },
    {
      key: "advisory",
      label: "Confidence",
      sortable: true,
      sortValue: (row) => confidenceRank(row.advisory),
      render: (row) => (
        <span
          style={{
            background: confidenceColors[row.advisory] || confidenceColors["N/A"],
            padding: "3px 6px",
            borderRadius: "4px",
            fontWeight: "bold",
          }}
        >
          {row.advisory}
        </span>
      ),
    },
    { key: "bright4", label: "Brightness temperature 4 (Kelvin)", sortable: true },
    { key: "bright5", label: "Brightness temperature 5 (Kelvin)", sortable: true },
    { key: "lat", label: "Lat", sortable: true },
    { key: "lng", label: "Long", sortable: true },
  ];

  return (
    <div className="firms-container">
      <div className="map-container-Firms">
        
        {loading && <CircleLoader />}
        
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall"
          maxZoom={14} 
          whenReady={(map) => { map.target.invalidateSize(); }}
        >
          <MapHomeControl />
          <MapBoundsUpdater bounds={mapBounds} />
          {/* Base Map Layers (Always visible) */}
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='Map data &copy; NASAs FIRMS Data'
          />

          {/* VIIRS Fire Detections (Always visible, data filtered by selectedDate) */}
          {showFirmsLayer && sucoFireReports.length > 0 && sucoFireReports.map((fire, index) => (
              <Marker
                  key={index}
                  position={[fire.latitude, fire.longitude]}
                  icon={createPulseIcon()}
              >
                  <Popup>
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
                      <h5 style={{ margin: '0 0 5px 0' }}>Fire Detection Details</h5>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Suco:</td><td style={{ padding: '3px' }}>{fire.ADM3}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Post Admin:</td><td style={{ padding: '3px' }}>{fire.ADM2}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Municipality:</td><td style={{ padding: '3px' }}>{fire.ADM1}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Date:</td><td style={{ padding: '3px' }}>{getFormattedDateForAPI(selectedDate)}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Time (UTC):</td><td style={{ padding: '3px' }}>{formatUTCTime(fire.acq_time)}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Confidence:</td><td style={{ padding: '3px' }}>{getConfidenceText(fire.confidence)}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Brightness Temp 4 (K):</td><td style={{ padding: '3px' }}>{fire.bright_ti4?.toFixed(2)}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Brightness Temp 5 (K):</td><td style={{ padding: '3px' }}>{fire.bright_ti5?.toFixed(2)}</td></tr>
                          <tr><td style={{ padding: '3px', fontWeight: 'bold' }}>Lat, Long:</td><td style={{ padding: '3px' }}>{fire.latitude?.toFixed(4)}, {fire.longitude?.toFixed(4)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Popup>
              </Marker>
          ))}


          {/* Filter Panel (Date Picker) */}
          <Box
            sx={{ position: "absolute", top: 10, left: 50, zIndex: 999 }}
            ref={filterPanelRef}
          >
            <IconButton
              color="primary"
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              data-filter-toggle
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "50%",
                  padding: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: 1,
                }}
              >
                <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 32 }} />
              </Box>
            </IconButton>

            <AnimatePresence>
              {showFilterPanel && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="FilterPanel" style={{ 
                      backgroundColor: 'white', 
                      padding: '10px', 
                      borderRadius: '5px', 
                      width: 'auto',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)', 
                      marginTop: '5px' 
                  }}>
                    <h6 style={{ margin: '0 0 10px 0' }}>Select Date:</h6>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                      <DatePicker
                        selected={selectedDate}
                        onChange={handleCalendarDateChange}
                        dateFormat="yyyy-MM-dd"
                        filterDate={isDateAvailable} 
                        placeholderText="Select a date"
                        className="dropdown"
                      />
                    </div>
                    {/* Checkboxes for Layer Toggling */}
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={showFirmsLayer} 
                                onChange={(e) => setShowFirmsLayer(e.target.checked)} 
                                color="primary" 
                            />
                        }
                        label="FIRMS Detections"
                        sx={{ fontSize: '12px' }}
                    />
                     <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={showForecastLayer} 
                                    onChange={(e) => setShowForecastLayer(e.target.checked)} 
                                    color="primary" 
                                />
                            }
                            label="Fire Risk Forecast"
                            sx={{ fontSize: '12px' }}
                        />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          <Information />

          {/* ⭐️ Layers Control (Contains togglable Overlays) ⭐️ */}
          <LayersControl position="topright">
            
            {/* Suco Boundary Layer (Always visible) */}
            {boundaryData && (
              <LayersControl.Overlay name="Suco Boundary" checked>
                <GeoJSON
                  data={boundaryData}
                  style={{
                    color: "#000000",
                    weight: 1,
                    opacity: 0.8,
                    fillOpacity: 0,
                  }}
                />
              </LayersControl.Overlay>
            )}

            {/* WMS Layer for Fire Risk Forecast */}
            {showForecastLayer && (
              <LayersControl.Overlay 
                name={`Fire Risk Forecast (${getFormattedDateForTIFF(selectedDate)})`} 
                checked={showForecastLayer} 
                onChange={(e) => setShowForecastLayer(e.target.checked)}
              >
                <WMSTileLayer
                  url={GEOSERVER_WMS_URL}
                  layers={`fire-risk:final_fire_risk_${getFormattedDateForTIFF(selectedDate)}_masked`}
                  format="image/png"
                  transparent={true}
                  version="1.1.0"
                  opacity={0.8}
                  key={getFormattedDateForTIFF(selectedDate)}
                />
              </LayersControl.Overlay>
            )}

          </LayersControl>
          
          <div 
            className="map-footer-right" 
            style={{ 
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              color: 'black',
              padding: '10px 15px',
              borderRadius: '5px',
              fontSize: '14px',
              textAlign: 'right',
              width: 'max-content',
              zIndex: 1000,
            }}
          >
            <p style={{ margin: 0 }}>{mapFooterDescription}</p>
          </div>

          <MapLegendControl
            sections={[
              { items: [{ label: "VIIRS Fire Detections", color: "red" }] },
              {
                items: [
                  { label: "No-Risk", color: "rgb(148, 203, 85)" },
                  { label: "Low-Risk", color: "rgb(243, 235, 18)" },
                  { label: "Medium-Risk", color: "rgb(243, 155, 58)" },
                  { label: "High-Risk", color: "rgb(230, 45, 58)" },
                ],
              },
            ]}
          />
        </MapContainer>
      </div>

      <Box sx={{ p: 2, borderTop: '1px solid #ddd' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          Fire Detections by Suco ({sucoFireReports.length} points)
        </Typography>
        <CommonTable
          key={getFormattedDateForAPI(selectedDate)}
          tableData={tableData}
          columns={columns}
          currentTimestamp={getFormattedDateForAPI(selectedDate)}
          showFilter
          filterOptions={["High", "Nominal", "Low", "All"]}
          legendColors={confidenceColors}
          defaultSort={{ key: "advisory", direction: "asc" }}
          searchableKeys={["Suco", "PostAdmin", "Municipality"]}
          tableName="FIRMS_Fire_Detections"
          searchPlaceholder="Search by Municipality, Post Admin, Suco..."
          emptyMessage="No fire detections found within Suco boundaries for the selected date."
        />
      </Box>
    </div>
  );
};

export default Firms;